import { useState, useEffect } from 'react';
import { Link } from 'prismic-reactjs';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import dynamic from 'next/dynamic';
import {
  DATE_LOCALES,
  getCurrentDateLocale,
  parseDate,
} from '../../../utils/dates';
import {
  getRealLocale, getVerticalByLink, prismicLinkResolver,
} from '../../../utils/common';
import { getColor } from '../../../utils/colors';

const LinkWithQuery = dynamic(() => import('../../../components/LinkWithQuery'), {
  ssr: false,
});
const ReactCarousel = dynamic(() => import('../../../components/ReactCarousel'), {
  ssr: false,
});

const dateFromat = (date: string, language: string): string => {
  const locale = getCurrentDateLocale(DATE_LOCALES, language);

  return parseDate(date, locale, 'd MMM');
};

const getAvailableItems = (items) => items?.filter(({ dateStart }) => {
  const date = new Date(dateStart).getTime();
  const now = new Date().getTime();

  return now <= date;
});

const carouselOptions = {
  isRTL: false,
  pagination: true,
};

const CarouselWithDate = ({ slice }) => {
  const items = getAvailableItems(slice?.items);
  const [lang, setLang] = useState<string>('es');
  const router = useRouter();

  useEffect(() => {
    if (router?.locale) setLang(getRealLocale(router.locale));
  }, [router]);

  return (
    <div className="text-center bg-white dark:bg-gray-1">
      <div className="dh-content">
        {slice.primary?.title && (
          <h2 className="dh-title">{slice.primary.title}</h2>
        )}

        <div className="my-8">
          {items?.length && (
            <ReactCarousel options={carouselOptions}>
              {items.map((item) => {
                const { color: verticalColor } = getVerticalByLink(
                  item.link,
                  slice._verticals ?? [slice._vertical],
                  slice._clusters ?? [slice._cluster],
                );
                const color = getColor(verticalColor);

                return (
                  <div
                    className={classNames(
                      'grid w-full h-full py-5 px-8 rounded-2xl border bg-gray-4 dark:bg-white',
                      'lg:px-6 xl:px-8 dark:border-none',
                      color.border,
                    )}
                    key={nanoid()}
                  >
                    {item.title && (
                      <h3 className="uppercase tracking-widest-xl text-white text-base dark:text-black">
                        {item.title}
                      </h3>
                    )}
                    {item.dateStart && (
                      <div
                        className={classNames(
                          'uppercase text-2xl my-4 font-bold',
                          `${color.text}`,
                        )}
                      >
                        <span>{dateFromat(item.dateStart, lang)}</span>
                        {item.dateEnd && (
                          <>
                            <span> - </span>
                            <span>{dateFromat(item.dateEnd, lang)}</span>
                          </>
                        )}
                      </div>
                    )}
                    <div className="text-left text-base text-white dark:text-black">
                      {item.days && (
                        <p className="font-semibold font-archivo">{item.days}</p>
                      )}
                      {item.hours && <p className="mt-3">{item.hours}</p>}
                    </div>

                    {item.link && item.linkLabel && (
                      <LinkWithQuery
                        href={Link.url(item.link, prismicLinkResolver)}
                        className={classNames(
                          'dh-button',
                          color.bg,
                          color.bgHover,
                        )}
                      >
                        {item.linkLabel}
                      </LinkWithQuery>
                    )}
                  </div>
                );
              })}
            </ReactCarousel>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarouselWithDate;
